/* straight from the module, not the `types` barrel: a runtime import of the barrel loads the app */
import { CustomerLink, CustomerLinkEdit, FleetAccount } from 'types/fleet-files';

/*
 * Pending edits in the bulk fleet-code editor, keyed by ACCOUNT.
 *
 * The backend's unit of change is the account: each account owns at most one link, with one final
 * code or none. Keying edits by account makes the save request a direct serialisation of this state,
 * and makes "move this account from code 7 to code 5" one entry instead of a removal on one card and
 * an addition on another that would have to be reconciled.
 */
export type LinkEdits = Map<number, { code: number | null; account: FleetAccount }>;

/* One account as a card shows it. */
export interface LinkedAccountView extends FleetAccount {
  /* this account's link differs from what the server holds */
  pending: boolean;
  /* linked here on the server, and pending removal — shown so the admin can see and undo it */
  removed?: boolean;
  /* the code it is moving away from, when it had one */
  movedFrom?: CustomerLink;
  /* linked here on the server, and pending a move to this code — shown so the source card isn't silent */
  movingTo?: CustomerLink;
}

export interface CustomerLinkView extends Omit<CustomerLink, 'customers'> {
  customers: LinkedAccountView[];
}

/* account id -> the code the server currently links it to */
export const committedCodes = (links: CustomerLink[]): Map<number, number> => {
  const codes = new Map<number, number>();
  links.forEach(link => link.customers.forEach(c => codes.set(c.id, link.code)));
  return codes;
};

/*
 * Records that an account should end up on `code` (null: unlinked). An edit that returns the account
 * to what the server already holds is dropped, so undoing a change leaves nothing to send.
 */
export const withEdit = (
  edits: LinkEdits,
  links: CustomerLink[],
  account: FleetAccount,
  code: number | null
): LinkEdits => {
  const next = new Map(edits);
  const committed = committedCodes(links).get(account.id) ?? null;
  if (committed === code) next.delete(account.id);
  else next.set(account.id, { code, account });
  return next;
};

/* The cards: the server's state with the pending edits laid over it. */
export const viewOf = (links: CustomerLink[], edits: LinkEdits): CustomerLinkView[] => {
  const committed = committedCodes(links);
  const byCode = new Map(links.map(l => [l.code, l]));
  return links.map(link => {
    /*
     * Every account the server links here stays on this card. One being unlinked is marked removed; one
     * moving to another code is marked with its destination, and also shown there as the move.
     */
    const kept: LinkedAccountView[] = link.customers.map(c => {
      const edit = edits.get(c.id);
      if (!edit) return { ...c, pending: false };
      if (edit.code === null) return { ...c, pending: true, removed: true };
      return { ...c, pending: true, movingTo: byCode.get(edit.code) };
    });
    const added: LinkedAccountView[] = [...edits.values()]
      .filter(e => e.code === link.code)
      .map(({ account }) => {
        const from = committed.get(account.id);
        return { ...account, pending: true, movedFrom: from === undefined ? undefined : byCode.get(from) };
      });
    return { code: link.code, name: link.name, customers: [...kept, ...added] };
  });
};

/* The save request: one entry per changed account, in a stable order. */
export const toRequest = (edits: LinkEdits): CustomerLinkEdit[] =>
  [...edits.entries()]
    .map(([customerId, { code }]) => ({ customerId, code }))
    .sort((a, b) => a.customerId - b.customerId);
